import Cookies from 'js-cookie'
import { constants } from './constants'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import axios from 'axios'
import { getRequestParams, getResponseParams } from './responseAndRequestHandlers'
import { jwtDecode } from 'jwt-decode'
import { redirectToLogin } from './helpers'

export const setAuthCookies = (
  idToken: string,
  refreshToken: string,
  authExpiry?: number,
  refreshExpiry?: number,
  intercomToken?: string
) => {
  const authExpiresDate = authExpiry && new Date(authExpiry)
  const refreshExpiresDate = refreshExpiry && new Date(refreshExpiry)

  try {
    Cookies.set(constants.AUTH_COOKIE_CLIENT, idToken, {
      expires: authExpiresDate ?? constants.AUTH_COOKIE_CLIENT_EXPIRY,
      sameSite: 'strict',
      secure: true
    })

    if (Boolean(refreshToken)) {
      Cookies.set(constants.REFRESH_TOKEN_COOKIE_CLIENT, refreshToken, {
        expires: refreshExpiresDate ?? constants.REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY,
        sameSite: 'strict',
        secure: true
      })
    } else {
      Cookies.set(constants.REFRESH_TOKEN_COOKIE_CLIENT, refreshToken, {
        expires: new Date(0),
        sameSite: 'none',
        secure: true
      })
      Cookies.remove(constants.REFRESH_TOKEN_COOKIE_CLIENT)
    }

    if (intercomToken) {
      Cookies.set(constants.FLO_INTERCOM_TOKEN, intercomToken, {
        expires: authExpiresDate ?? constants.AUTH_COOKIE_CLIENT_EXPIRY,
        sameSite: 'none',
        secure: true
      })
    }
  } catch (e: any) {
    console.error(e)
    throw new Error(e)
  }
}

export const isAccessTokenExpiringInNextFiveMinutesOrExpired = () => {
  try {
    const authToken = Cookies.get(constants.AUTH_COOKIE_CLIENT)
    if (!authToken) return true

    const { exp = 0 } = jwtDecode(authToken)
    const timeUntilExpiry = exp - Date.now() / 1000

    return timeUntilExpiry <= 5 * 60
  } catch (e) {
    console.error('Error decoding token: ', e)
    return true
  }
}

// Single-flight slot for the network refresh. The backend ROTATES the refresh
// token on every POST /merchant-user/refresh, so two concurrent refreshes race:
// the loser presents an already-rotated token, 401s, and force-logs the user
// out. Concurrent callers (the axios interceptor, per-request pre-flight
// checks, and copilot's authedFetch) must share one in-flight refresh.
let inFlightRefresh: Promise<boolean> | null = null

export const refreshExpiredIdToken = async (isForceRefresh?: boolean) => {
  const refreshToken = Cookies.get(constants.REFRESH_TOKEN_COOKIE_CLIENT)
  if (
    !Boolean(isForceRefresh) &&
    Cookies.get(constants.AUTH_COOKIE_CLIENT) &&
    !isAccessTokenExpiringInNextFiveMinutesOrExpired()
  )
    return true
  if (!Boolean(refreshToken)) return false

  if (inFlightRefresh) return inFlightRefresh

  inFlightRefresh = (async () => {
    try {
      const payload: any = {
        refresh_token: refreshToken
      }

      const { headers } = getRequestParams(false, apiURI.AUTH_ENGINE)
      const response = await axios.post(`${apiURI.AUTH_ENGINE}/merchant-user/refresh`, payload, { headers: headers })
      const adaptedResponse: any = getResponseParams(apiURI.AUTH_ENGINE, response)

      if (!adaptedResponse) return false

      const {
        access_token: newAuthToken,
        refresh_token: newRefreshToken,
        access_token_expires_at: newAuthTokenExpiry,
        refresh_token_expires_at: newRefreshTokenExpiry,
        intercom_jwttoken: newIntercomToken
      } = adaptedResponse?.data?.data || {}

      setAuthCookies(newAuthToken, newRefreshToken, newAuthTokenExpiry, newRefreshTokenExpiry, newIntercomToken)
      return true
    } catch (e: any) {
      console.error(e)
      redirectToLogin()
      Cookies.remove(constants.REFRESH_TOKEN_COOKIE_CLIENT)
      return false
    } finally {
      // Clear regardless of outcome so the next expiry starts a fresh refresh.
      inFlightRefresh = null
    }
  })()

  return inFlightRefresh
}
